
import { useSession, useSupabaseClient } from "@supabase/auth-helpers-react";
import { Database } from "@/integrations/supabase/types";
import { useToast } from "@/hooks/use-toast";
import { DevotionalRecord, DevotionalProgressRecord, ActionStep } from "../types";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export function useDevotionalData(currentDay: number, completedDays: number[], onCompletion?: () => void) {
  const session = useSession();
  const supabase = useSupabaseClient<Database>();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch devotional and progress data together
  const { data: devotionalData, isLoading } = useQuery({
    queryKey: ['devotional', currentDay, session?.user?.id],
    queryFn: async () => {
      try {
        // First fetch the devotional for the current day - this works for all users
        const { data: devotionalData, error: devotionalError } = await supabase
          .from('daily_devotionals')
          .select('*')
          .eq('day_number', currentDay)
          .limit(1)
          .maybeSingle();

        if (devotionalError) throw devotionalError;

        if (!devotionalData) {
          toast({
            title: "Devotional not found",
            description: `No devotional content available for day ${currentDay}`,
            variant: "destructive",
          });
          return null;
        }

        // Only fetch progress if user is authenticated
        let progressData = null;
        if (session?.user?.id) {
          const { data: progress, error: progressError } = await supabase
            .from('devotional_progress')
            .select('*')
            .eq('devotional_id', devotionalData.id)
            .eq('user_id', session.user.id)
            .maybeSingle();

          if (progressError) {
            console.error('Error fetching progress:', progressError);
          } else {
            progressData = progress;
          }
        }

        return {
          devotional: {
            ...devotionalData,
            action_steps: (devotionalData.action_steps as unknown as ActionStep[]) || []
          } as DevotionalRecord,
          progress: progressData ? {
            ...progressData,
            completed_actions: (progressData.completed_actions as unknown as ActionStep[]) || []
          } as DevotionalProgressRecord : null
        };
      } catch (error) {
        console.error('Error in fetchDevotional:', error);
        toast({
          title: "Error",
          description: "Failed to load devotional content",
          variant: "destructive",
        });
        return null;
      }
    },
    enabled: Boolean(currentDay) // Only require currentDay, not session
  });

  const isProgressCompleted = (progressData: DevotionalProgressRecord | null): boolean => {
    if (!progressData) return false;
    
    const basicFieldsCompleted = progressData.completed_at && 
                               progressData.completed_prayer && 
                               progressData.completed_reflection;
    
    if (!basicFieldsCompleted) return false;

    const allActionsCompleted = progressData.completed_actions &&
                               Array.isArray(progressData.completed_actions) &&
                               progressData.completed_actions.every(action => action.completed);

    return allActionsCompleted;
  };

  const showCompletion = devotionalData?.progress ? isProgressCompleted(devotionalData.progress) : false;

  return {
    devotional: devotionalData?.devotional || null,
    progress: devotionalData?.progress || null,
    showCompletion,
    isLoading
  };
}
